// Pure helper functions for the Report Assignment workspace. All data comes
// from the Report Assignment API; these only derive display state from it.
import {
  ModuleItem,
  ReportAssignmentRow,
  ReportRowState,
  ReportTypeDefinition,
} from "./types";

/** One-line description of a report type's module-selection rule. */
export function ruleDescription(type: ReportTypeDefinition): string {
  if (type.who === "all") return "Every module with test steps that passed inspection";
  if (type.who === "none") return "";
  return `Modules on the ${type.tsdName ?? "report's"} test`;
}

/** Row workflow state driving status chip + available actions. */
export function rowState(
  row: Pick<ReportAssignmentRow, "moduleIds" | "confirmed" | "progress">,
): ReportRowState {
  if (!row.progress.none && row.moduleIds.length === 0) return "needs_modules";
  return row.confirmed ? "confirmed" : "to_confirm";
}

export interface BomStats {
  total: number;
  confirmed: number;
  needsModules: number;
  readyToWrite: number;
}

/** Aggregate counters used for BOM tab badges, the footer and the summary bar. */
export function bomStats(rows: ReportAssignmentRow[]): BomStats {
  return {
    total: rows.length,
    confirmed: rows.filter((r) => r.confirmed).length,
    needsModules: rows.filter((r) => rowState(r) === "needs_modules").length,
    readyToWrite: rows.filter((r) => r.confirmed && r.progress.ready).length,
  };
}

/** Rows that have modules (or need none) and are waiting to be confirmed. */
export const readyToConfirmRows = (rows: ReportAssignmentRow[]): ReportAssignmentRow[] =>
  rows.filter((r) => !r.confirmed && rowState(r) === "to_confirm");

export const moduleLabelForTooltip = (m: ModuleItem | undefined): string =>
  m ? (m.tsds.length ? m.tsds.map((t) => t.name).join(", ") : "Spare") : "";

export interface ModuleIdDiff {
  add: number[];
  rem: number[];
}

/** What changed between two module-id selections. */
export function diffModuleIds(before: number[], after: number[]): ModuleIdDiff {
  return {
    add: after.filter((id) => !before.includes(id)),
    rem: before.filter((id) => !after.includes(id)),
  };
}

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** "12 Sep 2026" for an ISO date / date time, or "" when missing or invalid. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getDate()} ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
}

/** "Mar 1, 14:20" style timestamp used in the change history. */
export function formatLogTime(d: Date): string {
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  return `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** "Due 12 Sep 2026" or "No due date". */
export const dueLabel = (iso: string | null | undefined): string => {
  const text = formatDate(iso);
  return text ? `Due ${text}` : "No due date";
};

/** Earliest–latest due date of a set of rows, or "" when none has one. */
export function dueRangeLabel(rows: Pick<ReportAssignmentRow, "dueDate">[]): string {
  const times = rows
    .map((r) => (r.dueDate ? new Date(r.dueDate).getTime() : NaN))
    .filter((t) => !Number.isNaN(t))
    .sort((a, b) => a - b);
  if (times.length === 0) return "";
  const first = formatDate(new Date(times[0]).toISOString());
  const last = formatDate(new Date(times[times.length - 1]).toISOString());
  return first === last ? first : `${first} – ${last}`;
}

/** "X of Y" fraction (0-1) from a status string, or null. */
export function fractionFromStatus(status: string): number | null {
  const m = /([\d.]+)\s*of\s*([\d.]+)/.exec(status);
  if (!m || Number(m[2]) === 0) return null;
  return Math.min(1, Number(m[1]) / Number(m[2]));
}

/** Message of an API error response, falling back to a generic text. */
export function apiErrorMessage(error: any, fallback: string): string {
  return error?.response?.data?.message || error?.response?.data?.detail || fallback;
}
